import { useState } from 'react';
import { CopyVariant } from '../../consultantTypes';
import { CreditCard as Edit2, Check, Sparkles, CheckCircle, XCircle, Clock } from 'lucide-react';

interface CopyVariantCardProps {
  variant: CopyVariant;
  onToggleSelect: (id: string) => void;
  onEdit: (id: string, body: string) => void;
  disabled: boolean;
}

export function CopyVariantCard({ variant, onToggleSelect, onEdit, disabled }: CopyVariantCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editBody, setEditBody] = useState(variant.body);

  const handleSave = () => {
    onEdit(variant.id, editBody);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditBody(variant.body);
    setIsEditing(false);
  };

  const getStatusBadge = () => {
    if (!variant.status || variant.status === 'pending') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
          <Clock className="w-3 h-3" />
          Te reviewen
        </span>
      );
    }

    if (variant.status === 'approved') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
          <CheckCircle className="w-3 h-3" />
          Goedgekeurd
        </span>
      );
    }

    if (variant.status === 'rejected') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
          <XCircle className="w-3 h-3" />
          Afgekeurd
        </span>
      );
    }
  };

  return (
    <div className={`bg-white rounded-2xl shadow-md p-5 border-2 transition-all ${
      variant.selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
    }`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-gray-900">{variant.title}</h3>
          {variant.source === 'AI' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
              <Sparkles className="w-3 h-3" />
              AI
            </span>
          )}
          {getStatusBadge()}
        </div>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Bewerken"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {isEditing ? (
        <div>
          <textarea
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
            rows={5}
          />
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={handleSave}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Opslaan
            </button>
            <button
              onClick={handleCancel}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuleren
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-gray-700 text-sm leading-relaxed mb-4 whitespace-pre-wrap">
            {variant.body}
          </p>

          {variant.feedback && (
            <div className={`mb-4 p-3 rounded-lg ${
              variant.status === 'rejected'
                ? 'bg-red-50 border border-red-200'
                : 'bg-blue-50 border border-blue-200'
            }`}>
              <p className={`text-xs font-semibold mb-1 ${
                variant.status === 'rejected' ? 'text-red-800' : 'text-blue-800'
              }`}>
                {variant.status === 'rejected' ? 'Feedback van klant:' : 'Verwerkte feedback:'}
              </p>
              <p className={`text-sm ${
                variant.status === 'rejected' ? 'text-red-700' : 'text-blue-700'
              }`}>
                {variant.feedback}
              </p>
              {variant.status === 'pending' && (
                <p className="text-xs text-blue-600 mt-2 italic">
                  In afwachting van nieuwe beoordeling door klant
                </p>
              )}
            </div>
          )}

          <button
            onClick={() => onToggleSelect(variant.id)}
            disabled={disabled && !variant.selected}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              variant.selected
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : disabled
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {variant.selected && <Check className="w-4 h-4" />}
            {variant.selected ? 'Geselecteerd' : 'Selecteer'}
          </button>
        </>
      )}
    </div>
  );
}
